// src/data/visibility.js
//
// Declarative visibility DSL evaluator, per `docs/architecture/data-model.md`
// §4.  Replaces the hardcoded branching in the old `isQuestionVisible` so
// org admins can author their own visibility rules via the questionnaire
// editor (ticket 06).
//
// Grammar (see design doc §4.1):
//
//   Rule =
//     | { equals:     { questionId: string, value: scalar } }
//     | { notEquals:  { questionId: string, value: scalar } }
//     | { includes:   { questionId: string, value: string } }   // multi-select
//     | { startsWith: { questionId: string, value: string } }
//     | { not: Rule }
//     | { and: Rule[] }
//     | { or:  Rule[] }
//
// Semantics (§4.2):
//   - No rule → visible.
//   - Referenced answer is undefined/null → every operator returns false.
//     This mirrors the old behavior: conditional questions only appear
//     *after* the gating question is answered.
//   - `and: []` → true, `or: []` → false. Vacuous truth for identity.
//   - Unknown operator → false + one dev-console warning. Never throws.
//   - Pure and stateless — safe to call inside React render.

const warned = new Set();

function warnOnce(key, message) {
  if (warned.has(key)) return;
  warned.add(key);
  // eslint-disable-next-line no-console
  console.warn(`[visibility] ${message}`);
}

function getAnswer(responses, questionId) {
  if (!responses) return undefined;
  return responses[questionId];
}

function hasAnswer(value) {
  if (value === undefined || value === null) return false;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === 'string') return value.length > 0;
  return true;
}

function evalRule(rule, responses) {
  if (!rule || typeof rule !== 'object') return true;

  if (rule.equals) {
    const { questionId, value } = rule.equals;
    const answer = getAnswer(responses, questionId);
    if (!hasAnswer(answer)) return false;
    return answer === value;
  }

  if (rule.notEquals) {
    const { questionId, value } = rule.notEquals;
    const answer = getAnswer(responses, questionId);
    if (!hasAnswer(answer)) return false;
    return answer !== value;
  }

  if (rule.includes) {
    const { questionId, value } = rule.includes;
    const answer = getAnswer(responses, questionId);
    if (!hasAnswer(answer)) return false;
    if (!Array.isArray(answer)) return false;
    return answer.includes(value);
  }

  if (rule.startsWith) {
    const { questionId, value } = rule.startsWith;
    const answer = getAnswer(responses, questionId);
    if (!hasAnswer(answer)) return false;
    const s = typeof answer === 'string' ? answer : String(answer);
    return s.startsWith(value);
  }

  if (rule.not) {
    return !evalRule(rule.not, responses);
  }

  if (Array.isArray(rule.and)) {
    // Vacuous truth: and: [] → true.
    return rule.and.every((r) => evalRule(r, responses));
  }

  if (Array.isArray(rule.or)) {
    // Vacuous falsity: or: [] → false.
    return rule.or.some((r) => evalRule(r, responses));
  }

  // Unknown operator shape. Log once per operator key and hide the
  // question so bad data doesn't leak into the UI.
  const opKey = Object.keys(rule).join(',');
  warnOnce(opKey, `unknown rule operator: ${opKey}`);
  return false;
}

/**
 * Public: returns true if a question should be visible given the current
 * responses. `rule` may be undefined (always visible).
 */
export function isVisible(rule, responses) {
  if (!rule) return true;
  return evalRule(rule, responses);
}

/**
 * Test hook: clears the warn-once cache so tests can assert on repeated
 * warnings. Not exported from the module barrel.
 */
export function __resetWarnCache() {
  warned.clear();
}
